import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Mail, Lock, Eye, EyeOff, AlertCircle, LogIn } from 'lucide-react';
import { adminAuthService } from '../../services/adminAuthService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import AuthLayout from '../../layouts/AuthLayout';
import Loader from '../../components/ui/Loader';
import { useToast } from '../../contexts/ToastContext';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lastSupabaseError, setLastSupabaseError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(''); // Clear error when user starts typing
  };

  const validateForm = () => {
    if (!formData?.email) {
      setError('Email is required');
      return false;
    }
    if (!formData?.password) {
      setError('Password is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setLastSupabaseError(null); // Clear previous error

    if (!formData?.email?.trim() || !formData?.password?.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await adminAuthService?.signIn(formData?.email, formData?.password);
      showToast('Login successful!', 'success');
      navigate('/admin-dashboard');
    } catch (error) {
      console.error('Login error:', error);
      setLastSupabaseError(error?.message || error); // Store error for debug
      setError(error?.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Portal</h1>
          <p className="text-gray-600">Secure access for administrative personnel</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Debug Helpers - Only shown when VITE_DEBUG_MODE=true */}
            {import.meta.env?.VITE_DEBUG_MODE === "true" && (
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <p className="text-sm font-bold text-yellow-900">🐛 DEBUG MODE ACTIVE</p>
                </div>
                
                {/* Button Status Indicator */}
                <div className="bg-white rounded p-2 border border-yellow-200">
                  <p className="text-xs font-semibold text-gray-700">Button State:</p>
                  <p className="text-sm text-gray-900">
                    Loader active: <span className={isSubmitting ? "text-green-600 font-bold" : "text-red-600 font-bold"}>
                      {isSubmitting ? "TRUE" : "FALSE"}
                    </span>
                  </p>
                </div>

                {/* Supabase Error Display */}
                {lastSupabaseError && (
                  <div className="bg-red-50 border border-red-300 rounded p-2">
                    <p className="text-xs font-semibold text-red-700">Last Supabase Error:</p>
                    <p className="text-xs text-red-900 break-words">{lastSupabaseError}</p>
                  </div>
                )}

                {/* General Error Display */}
                {error && (
                  <div className="bg-orange-50 border border-orange-300 rounded p-2">
                    <p className="text-xs font-semibold text-orange-700">Error Message:</p>
                    <p className="text-xs text-orange-900">{error}</p>
                  </div>
                )}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            )}

            {/* Email Input */}
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                Admin Email
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                  placeholder="admin@phangantaxi.com"
                  className="pl-11 w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                Password
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData?.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  className="pl-11 pr-11 w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isSubmitting}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-4 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed' :'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg hover:shadow-xl hover:scale-[1.02]'
              }`}
            >
              {isSubmitting ? (
                <Loader text="Signing in..." size="sm" className="text-white" />
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </form>

          {/* Demo Credentials */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">Demo Admin Credentials</p>
              <div className="space-y-1 text-sm text-blue-800">
                <p>Email: admin@phangantaxi.com</p>
                <p className="text-xs text-blue-600 italic">Password: Use the password set during registration</p>
              </div>
            </div>
          </div>

          {/* Footer Links */}
          <div className="mt-6 text-center space-y-2">
            <p className="text-sm text-gray-600">
              Don't have admin access?{' '}
              <Link to="/admin-registration" className="text-blue-600 hover:text-blue-700 font-medium">
                Register as Admin
              </Link>
            </p>
            <Link to="/home-landing" className="text-sm text-gray-600 hover:text-gray-900 block">
              ← Back to Home
            </Link>
          </div>

          {/* Navigation Links */}
          <div className="text-center pt-4 border-t border-gray-200">
            <div className="flex items-center justify-center gap-4">
              <Link
                to="/"
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                Back to Home
              </Link>
              <span className="text-gray-300">•</span>
              <Link
                to="/role-selection"
                className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                Back to Role Selection
              </Link>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            🔒 This is a secure administrative portal. All activities are monitored and logged.
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}